import { APIKit } from "config/axios";
import store from "config/redux";
import { end, start } from "config/redux/slice";

import { RouteConstants, ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import { AgentTargetRequest, AgentTargetResponse, TargetOptionsResponse, TargetRequest, TargetResponse } from "types";
import { AppUtils } from "utils";

import { handleServiceError } from "./common";
import * as PublicService from "./public";

const { ROUTE_LOGIN } = RouteConstants;
const { API_V1_ADMIN_TARGETS, API_V1_ADMIN_TARGETS_AGENTS, API_V1_ADMIN_TARGETS_OPTIONS } = ServerConstants;
const {
  REQUEST_TARGETS_GET_ID,
  REQUEST_TARGETS_SAVE_ID,
  REQUEST_TARGETS_OPTIONS_GET_ID,
} = ServiceConstants;

const buildRequestId = (type: string) => `${REQUEST_TARGETS_SAVE_ID}${type}`;
const buildAgentGetRequestId = (agentId: number) => `${REQUEST_TARGETS_GET_ID}_AGENT_${agentId}`;
const buildAgentRequestId = (agentId: number) => `${REQUEST_TARGETS_SAVE_ID}AGENT_${agentId}`;
const buildAgentKpiTargetsEndpoint = (agentId: number) =>
  `${API_V1_ADMIN_TARGETS_AGENTS}/${agentId}/kpi`;

async function retryWithRenewal<T>(request: () => Promise<T>, retryOnUnauthorized = true): Promise<T> {
  try {
    return await request();
  } catch (err: any) {
    const errStatus = JSON.parse(JSON.stringify(err))?.status;

    if (errStatus === 401 && retryOnUnauthorized) {
      const renewed = await PublicService.renew();

      if (renewed) {
        return retryWithRenewal(request, false);
      }

      AppUtils.logout();
      if (window.location.pathname !== ROUTE_LOGIN) {
        window.location.href = ROUTE_LOGIN;
      }
    }

    throw err;
  }
}

async function performTrackedRequest<T extends object | undefined>(
  requestId: string,
  request: () => Promise<T>,
): Promise<T> {
  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(request);
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getTargets(): Promise<TargetResponse[]> {
  return performTrackedRequest(REQUEST_TARGETS_GET_ID, async () => {
    const response = await APIKit.get(API_V1_ADMIN_TARGETS);
    return response.data as TargetResponse[];
  });
}

export async function getTargetOptions(): Promise<TargetOptionsResponse> {
  return performTrackedRequest(REQUEST_TARGETS_OPTIONS_GET_ID, async () => {
    const response = await APIKit.get(API_V1_ADMIN_TARGETS_OPTIONS);
    return response.data as TargetOptionsResponse;
  });
}

export async function getAgentTarget(agentId: number): Promise<AgentTargetResponse | undefined> {
  const requestId = buildAgentGetRequestId(agentId);

  try {
    store.dispatch(start(requestId));
    const response = await retryWithRenewal(() => APIKit.get(buildAgentKpiTargetsEndpoint(agentId)));
    store.dispatch(end(new ServiceModel(requestId, response.data)));
    return response.data as AgentTargetResponse;
  } catch (err: any) {
    const status = err?.status ?? err?.response?.status;

    if (status === 404) {
      store.dispatch(end(new ServiceModel(requestId)));
      return;
    }

    handleServiceError(requestId, err);
    throw err;
  }
}

export async function saveTarget(data: TargetRequest): Promise<TargetResponse> {
  return performTrackedRequest(buildRequestId(data.type), async () => {
    const response = await APIKit.put(API_V1_ADMIN_TARGETS, data);
    return response.data as TargetResponse;
  });
}

export async function saveAgentTarget(
  agentId: number,
  data: AgentTargetRequest,
): Promise<AgentTargetResponse> {
  return performTrackedRequest(buildAgentRequestId(agentId), async () => {
    const response = await APIKit.put(buildAgentKpiTargetsEndpoint(agentId), data);
    return response.data as AgentTargetResponse;
  });
}
